import { util, type Canvas } from 'fabric';
import { getLayerObjects, reorderLayers, type LayerObject } from './CanvasLayers';

// Properties that fabric doesn't serialize by default but that we need to restore each object's exact
// state (animation, layer name, hidden/locked, the template/SKU it came from, the url of its font).
// Shared with clone() so copies keep them too.
export const CANVAS_CUSTOM_PROPS = [
  'layerName',
  'animationConfig',
  'lockMovementX',
  'lockMovementY',
  'lockScalingX',
  'lockScalingY',
  'lockRotation',
  'lockSkewingX',
  'lockSkewingY',
  'hasControls',
  'editable',
  'visible',
  'sourceRef',
  'fontUrl',
];

export interface CanvasDocumentState {
  backgroundColor: string;
  canvasWidth: number;
  canvasHeight: number;
  canvasSizeId: string;
}

export interface HistorySnapshot extends CanvasDocumentState {
  objects: object[];
}

export interface HistoryEntry {
  label: string;
  timestamp: number;
  snapshot: HistorySnapshot;
}

// Serializes an object leaving out the properties still at fabric's defaults (enlivening fills them back),
// which keeps both the in-memory history and the saved document small. The origin is always kept so a
// saved document doesn't depend on fabric's default origin staying the same across versions.
export const serializeLayer = (object: LayerObject): Record<string, unknown> => {
  const includeDefaultValues = object.includeDefaultValues;
  object.includeDefaultValues = false;
  try {
    return { ...object.toObject(CANVAS_CUSTOM_PROPS), originX: object.originX, originY: object.originY };
  } finally {
    object.includeDefaultValues = includeDefaultValues;
  }
};

// Captures the user objects (excluding grid/safe zone, which aren't part of the history)
// plus the document state (background, size), in fabric's internal order (back -> front).
export const captureHistorySnapshot = (canvas: Canvas, documentState: CanvasDocumentState): HistorySnapshot => ({
  ...documentState,
  objects: getLayerObjects(canvas).map(serializeLayer),
});

// Rebuilds the user objects from the snapshot and reinserts them preserving
// the z-index relative to the grid/safe zone (which are never touched during undo/redo).
export const restoreHistorySnapshot = async (canvas: Canvas, snapshot: HistorySnapshot) => {
  canvas.discardActiveObject();
  getLayerObjects(canvas).forEach((object) => canvas.remove(object));

  const restored = (await util.enlivenObjects(snapshot.objects)) as LayerObject[];
  restored.forEach((object) => canvas.add(object));
  reorderLayers(canvas, restored);

  canvas.backgroundColor = snapshot.backgroundColor;
  canvas.requestRenderAll();
};
